import { useMemo } from "react";
import { DateTime } from "luxon";

import type { LivingAirTemperatureSensor } from "@core/apis/shared/types/sensors.model";

import { getLastValidValue, sanitizeSensorValue } from "../sensorUtils";
import { AVERAGE_SENSOR_UID } from "./SensorOverview";

export type SensorReading = {
  timestamp: string;
  values: {
    living_air_temperature?: number;
    indoor_relative_humidity?: number;
  };
};

export type Building = {
  buildingId: string;
  name: string;
  sensors: Map<string, { sensor: LivingAirTemperatureSensor; lastReading: SensorReading | null }>;
};

export type HumidityStatus =
  { type: "none" } | { type: "available"; value: number; timestamp: string } | { type: "missing" };

export type SensorOverviewData = {
  buildings: Building[];
  allFloors: string[];
  avgTemp: number;
  avgTimestamp: DateTime;
  humidityStatus: HumidityStatus;
  activeSensors: number;
  totalSensors: number;
};

export function useSensorOverviewData(
  sensors: LivingAirTemperatureSensor[],
  avgSensor: LivingAirTemperatureSensor | null
): SensorOverviewData {
  return useMemo(() => {
    const sensorsWithReadings = sensors
      .filter((sensor) => sensor.uid !== AVERAGE_SENSOR_UID)
      .map((sensor) => {
        const lastReading =
          sensor.data && sensor.data.length > 0 ? sensor.data[sensor.data.length - 1] : null;
        return { sensor, lastReading };
      });

    // Humidity status detection
    const humidityStatus = ((): HumidityStatus => {
      const hasHumiditySensors = sensors.some((sensor) =>
        sensor.data?.some(
          (reading) =>
            reading.values.indoor_relative_humidity !== undefined &&
            reading.values.indoor_relative_humidity !== null
        )
      );

      if (!hasHumiditySensors) {
        return { type: "none" };
      }

      const avgHumidityValue =
        avgSensor?.data?.[avgSensor.data.length - 1]?.values.indoor_relative_humidity;

      if (avgHumidityValue !== undefined && avgHumidityValue !== null) {
        return {
          type: "available",
          value: avgHumidityValue,
          timestamp: avgSensor.data[avgSensor.data.length - 1].timestamp,
        };
      }

      return { type: "missing" };
    })();

    // Active sensor count (last reading within 2 hours)
    const now = DateTime.now();
    const activeSensors = sensorsWithReadings.filter(({ lastReading }) => {
      if (!lastReading?.timestamp) return false;
      const readingTime = DateTime.fromISO(lastReading.timestamp);
      return now.diff(readingTime, "hours").hours <= 2;
    }).length;

    // Group sensors by building
    const buildingMap = new Map<string, Building>();
    const floorSet = new Set<string>();

    sensorsWithReadings.forEach(({ sensor, lastReading }) => {
      const buildingId = sensor.street_address || "N/A";
      const floor = sensor.floor || "N/A";

      floorSet.add(floor);

      if (!buildingMap.has(buildingId)) {
        buildingMap.set(buildingId, {
          buildingId,
          name: buildingId,
          sensors: new Map(),
        });
      }

      buildingMap.get(buildingId)!.sensors.set(sensor.uid, { sensor, lastReading });
    });

    // Sort floors (N/A at the bottom)
    const allFloors = Array.from(floorSet).sort((a, b) => {
      if (a === "N/A" && b === "N/A") return 0;
      if (a === "N/A") return 1;
      if (b === "N/A") return -1;
      return Number(b) - Number(a);
    });

    // Average temperature from avg sensor
    const getAvgTempAndTimestamp = () => {
      if (!avgSensor?.data?.length) {
        return { avgTemp: NaN, avgTimestamp: DateTime.now() };
      }
      const temperatures = avgSensor.data.map((reading) =>
        sanitizeSensorValue(reading.values.living_air_temperature)
      );
      const lastValidTemp = getLastValidValue(temperatures);
      const lastReading = avgSensor.data[avgSensor.data.length - 1];

      return {
        avgTemp: lastValidTemp,
        avgTimestamp: DateTime.fromISO(lastReading.timestamp || ""),
      };
    };

    const { avgTemp, avgTimestamp } = getAvgTempAndTimestamp();

    return {
      buildings: Array.from(buildingMap.values()),
      allFloors,
      avgTemp,
      avgTimestamp,
      humidityStatus,
      activeSensors,
      totalSensors: sensorsWithReadings.length,
    };
  }, [sensors, avgSensor]);
}
